import React, {
    useContext, useEffect, useRef, useState
} from 'react';
import {
    useLocation, useNavigate, useParams
} from 'react-router-dom';
import {
    ArrowRight, Building2, Check, ChevronDown, ChevronRight, Settings
} from 'lucide-react';
import {
    Context
} from 'shared/context/GlobalState';
import 'builders/destudio/ui/tokens.css';

/**
 * The DE Studio org + sub-org pills, reused on 3D App Studio's platform header.
 * Token styles live on [data-theme="destudio"] — this cluster carries that
 * attribute so the pills look identical outside DE Studio's shell.
 */
export default function StudioOrgPills() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { oragID, subOragID } = useParams();
    const { organizations } = useContext(Context);
    const [storedSub, setStoredSub] = useState(
        () => (typeof localStorage !== 'undefined' ? localStorage.getItem('selectedSubOrg') : null)
    );

    const currentOrg = organizations?.find((o) => o._id === oragID);
    const onAppStudio = pathname.includes('/3dapp-studio');
    const subs = currentOrg?.subOrganizations || [];
    const resolvedSubId = subOragID
        || (storedSub && subs.some((s) => s._id === storedSub) ? storedSub : null)
        || subs.find((s) => s._id)?._id
        || null;
    const currentSubOrg = subs.find((s) => s._id === resolvedSubId);

    const pickOrg = (o) => {
        if (onAppStudio) navigate(`/organizations/${o._id}/3dapp-studio`);
        else navigate(`/organizations/${o._id}`);
    };
    const pickSub = (s) => {
        localStorage.setItem('selectedSubOrg', s._id);
        setStoredSub(s._id);
        if (!onAppStudio) navigate(`/organizations/${oragID}/sub/${s._id}`);
    };

    return (
        <div data-theme="destudio" className="flex items-center" style={{ gap: 4, minWidth: 0 }}>
            <OrgPill
                org={currentOrg}
                organizations={organizations}
                onPick={pickOrg}
                onSettings={() => oragID && navigate(`/organizations/${oragID}/settings`)}
            />
            {currentSubOrg && (
                <>
                    <BreadcrumbChevron />
                    <SubOrgPill
                        subOrg={currentSubOrg}
                        org={currentOrg}
                        onPick={pickSub}
                        onSettings={() => navigate(`/organizations/${oragID}/sub/${currentSubOrg._id}/settings`)}
                    />
                </>
            )}
        </div>
    );
}

function OrgPill({
    org, organizations = [], onPick, onSettings
}) {
    return (
        <ContextPill
            icon={Building2}
            label={org?.name || 'Pick organization'}
            kind="Organization"
            list={organizations}
            currentId={org?._id}
            onPick={onPick}
            onSettings={onSettings}
            settingsLabel="Manage organization"
            emptyText="No organizations yet."
        />
    );
}

function SubOrgPill({
    subOrg, org, onPick, onSettings
}) {
    return (
        <ContextPill
            icon={Building2}
            label={subOrg?.name || 'Pick sub-organization'}
            kind="Sub-organization"
            list={org?.subOrganizations || []}
            currentId={subOrg?._id}
            onPick={onPick}
            onSettings={onSettings}
            settingsLabel="Sub-organization settings"
            emptyText="No sub-organizations."
            tone="subtle"
        />
    );
}

function BreadcrumbChevron() {
    return (
        <ChevronRight
            size={13}
            style={{
                color: 'var(--ds-text-subtle)',
                flexShrink: 0,
                margin: '0 -2px'
            }}
        />
    );
}

function ContextPill({
    icon: Icon, label, kind, list, currentId, onPick, onSettings,
    settingsLabel, emptyText, tone,
}) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div ref={wrapRef} style={{
            position: 'relative',
            flexShrink: 0
        }}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="ds-context-pill"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <Icon size={13} style={{
                    color: tone === 'subtle' ? 'var(--ds-text-muted)' : 'var(--ds-primary)'
                }} />
                <span style={{
                    fontWeight: 600,
                    color: 'var(--ds-text)',
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {label}
                </span>
                <ChevronDown size={12} style={{
                    color: 'var(--ds-text-muted)',
                    transition: 'transform var(--ds-dur) var(--ds-ease)',
                    transform: open ? 'rotate(180deg)' : 'rotate(0)'
                }} />
            </button>

            {open && (
                <div
                    role="menu"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        minWidth: 280,
                        maxWidth: 360,
                        background: 'var(--ds-surface)',
                        border: '1px solid var(--ds-border)',
                        borderRadius: 'var(--ds-radius-lg)',
                        boxShadow: 'var(--ds-shadow-lg)',
                        zIndex: 60,
                        padding: 6,
                        animation: 'ds-pop-in var(--ds-dur-fast) var(--ds-ease)'
                    }}
                >
                    <div style={{
                        padding: '6px 10px 4px',
                        fontSize: 'var(--ds-text-2xs)',
                        fontWeight: 600,
                        color: 'var(--ds-text-subtle)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em'
                    }}>
                        {kind}
                    </div>

                    {list.length === 0 ? (
                        <div style={{
                            padding: '12px 10px',
                            fontSize: 'var(--ds-text-sm)',
                            color: 'var(--ds-text-muted)'
                        }}>
                            {emptyText}
                        </div>
                    ) : (
                        <div style={{
                            maxHeight: 280,
                            overflowY: 'auto'
                        }}>
                            {list.map((item) => (
                                <button
                                    key={item._id}
                                    type="button"
                                    onClick={() => { onPick?.(item); setOpen(false); }}
                                    className="ds-context-row"
                                >
                                    <span style={{
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {item.name}
                                    </span>
                                    {item._id === currentId && (
                                        <Check size={13} style={{
                                            color: 'var(--ds-primary)',
                                            flexShrink: 0
                                        }} />
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {onSettings && (
                        <>
                            <div style={{
                                height: 1,
                                background: 'var(--ds-divider)',
                                margin: '6px 4px'
                            }} />
                            <button
                                type="button"
                                onClick={() => { onSettings(); setOpen(false); }}
                                className="ds-context-footer"
                            >
                                <Settings size={13} />
                                <span style={{
                                    flex: 1,
                                    textAlign: 'left'
                                }}>{settingsLabel}</span>
                                <ArrowRight size={12} className="ds-context-footer-arrow" />
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
